import { AnimatePresence, motion } from "motion/react";
import {
  useEffect,
  useMemo,
  type FC,
  type ReactElement,
  type ReactNode,
} from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  FiltersActions,
  FiltersActionsApply,
  FloatingMenuGrid,
  TableWrapperGeneric,
  useTableColumnsDynamic,
} from "@ryde/controls";
import {
  BulkUpdateFormDrawer,
  ClientSideBulkOperationDrawerFlow,
  FinalSchemaService,
  PageHeader,
  PageHeaderFilters,
  TableSettingsDrawerWrapperGeneric,
} from "@ryde/layout";
import {
  PageHeaderContext,
  useDynamicFormsFetch,
  usePageContext,
  usePageTitle,
  type IPageHeaderContext,
} from "@ryde/redux";
import {
  bulkOperationsDrawerAF,
  bulkUpdateFormDrawerAF,
  isEntityServerSideFiltered,
  Log,
  selectedTableRowsIdsAtomFamily,
  tableSettingsDrawerAF,
  topCalendarDatesAtomFamily,
  type IColumnDef,
  type IExtendableDto,
  type ITableDto,
} from "@ryde/shared";

import { useStationsCreatedStations, useStationsGridSplitter } from "./hooks";
import {
  StationsGridMap,
  StationsGridTableWrapperStyled,
  StationsPanelTop,
} from "./panels";
import { StationsHeaderRightActions } from "./stations.header-right-actions";

interface IStationsPageContentProps {
  customRightComponent?: ReactElement | null;
  topWidgetComponent?: ReactNode;
}

export const StationsPageContent: FC<IStationsPageContentProps> = ({
  customRightComponent,
  topWidgetComponent,
}) => {
  const { entity } = usePageContext();
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const isServerSideFilteredEntity = isEntityServerSideFiltered(entity);

  const tableSettingsDrawerOpened = useRecoilValue(
    tableSettingsDrawerAF(entity),
  );
  const bulkOperationsDrawerOpened = useRecoilValue(
    bulkOperationsDrawerAF(entity),
  );
  const selectedIds = useRecoilValue(selectedTableRowsIdsAtomFamily(entity));
  const bulkUpdateFormDrawerOpened = useRecoilValue(
    bulkUpdateFormDrawerAF(entity),
  );
  const { initDynamicGridFetch, fetchTableRows } = useDynamicFormsFetch(entity);
  const { tableDefsGeneric } = useTableColumnsDynamic();
  const { pageTitleTranslated } = usePageTitle();

  const onFiltersApply = useEvent((): void => {
    fetchTableRows();
  });

  const headerContextValue: IPageHeaderContext = useMemo(() => {
    return {
      onFiltersApply,
      onFiltersClear: onFiltersApply,
      onSearchApply: onFiltersApply,
    };
  }, [onFiltersApply]);

  useEffect(() => {
    initDynamicGridFetch();
    Log.debug(
      "calendarDates changed for client-side Date filters",
      calendarDates,
    );
  }, [initDynamicGridFetch, calendarDates]);

  const {
    mapOpen,
    x,
    onDragStartHandler,
    onDragEndHandler,
    gridSizeProps,
    mapSizeProps,
    dragConstraints,
  } = useStationsGridSplitter();
  useStationsCreatedStations();

  return (
    <>
      <FinalSchemaService />
      <PageHeader
        mainTitle={pageTitleTranslated}
        customRightComponent={customRightComponent}
        customHeaderBottomRightActions={<StationsHeaderRightActions />}
        topWidgetComponent={topWidgetComponent}
        bottomWidgetComponent={<StationsPanelTop />}
      >
        <PageHeaderContext.Provider value={headerContextValue}>
          <PageHeaderFilters
            Actions={
              isServerSideFilteredEntity ? (
                <FiltersActionsApply />
              ) : (
                <FiltersActions />
              )
            }
          />
        </PageHeaderContext.Provider>
      </PageHeader>

      <StationsGridTableWrapperStyled>
        <motion.div className="grid_panel thin_scrollbar" style={gridSizeProps}>
          <TableWrapperGeneric
            tableDefs={
              tableDefsGeneric as IColumnDef<IExtendableDto, unknown>[]
            }
          />
        </motion.div>

        <AnimatePresence>
          {mapOpen && (
            <motion.div
              className="splitter"
              key="splitter"
              dragElastic={0}
              dragMomentum={false}
              onDragStart={onDragStartHandler}
              onDragEnd={onDragEndHandler}
              dragConstraints={dragConstraints}
              drag="x"
              style={{
                cursor: "col-resize",
                x,
              }}
            />
          )}

          {mapOpen && (
            <motion.div
              key="map_panel"
              className="map_panel"
              style={mapSizeProps}
            >
              <StationsGridMap />
            </motion.div>
          )}
        </AnimatePresence>
      </StationsGridTableWrapperStyled>

      {tableSettingsDrawerOpened && (
        <TableSettingsDrawerWrapperGeneric<ITableDto>
          tableDefs={tableDefsGeneric}
        />
      )}
      {selectedIds && <FloatingMenuGrid />}
      {bulkUpdateFormDrawerOpened && <BulkUpdateFormDrawer />}

      {bulkOperationsDrawerOpened && <ClientSideBulkOperationDrawerFlow />}
    </>
  );
};
